import { useRef, useCallback, useEffect } from 'react';
import { useGeoComply, GeoComplyCheckResult, GeoComplyFailureReason } from '@/hooks/useGeoComply';
import { performGeolocationCheck } from '@/lib/geocomplyUtils';
import { storage } from '@/lib/storage';

/**
 * Hook for managing geolocation check timing
 * 
 * This hook implements the requirement: "Every 20 mins since the last check"
 * 
 * KEY BEHAVIOR:
 * - When a check is performed, it records the timestamp
 * - The next check is scheduled 20 minutes after the last check (not from login)
 * - If a check happens at 5 minutes (e.g., gameplay), the next check is 20 minutes after that (at 25 minutes)
 * - Last check time is persisted in localStorage to survive page refreshes
 * 
 * USAGE:
 * This hook should be used by GeoComplyProvider to manage periodic checks.
 * Individual components (login, purchase, etc.) should call performCheck() directly
 * which will automatically update the timing.
 */
const LAST_CHECK_TIME_KEY = 'geocomply_last_check_time';
const LAST_CHECK_COMPLIANT_KEY = 'geocomply_last_check_compliant';

export function useGeoComplyTiming() {
  const { checkGeolocation, isInitialized } = useGeoComply();
  const lastCheckTimeRef = useRef<number | null>(null);
  const lastCheckCompliantRef = useRef<boolean | null>(null);
  const nextCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const checkIntervalMinutes = 20; // 20 minutes since last check
  const periodicCheckCallbackRef = useRef<((isCompliant: boolean, locationData?: GeoComplyCheckResult['location'], failureReasons?: GeoComplyFailureReason[]) => void) | null>(null);

  /**
   * Load the last check from localStorage on initialization
   */
  useEffect(() => {
    try {
      const stored = storage.getItem(LAST_CHECK_TIME_KEY);
      if (stored) {
        const timestamp = parseInt(stored, 10);
        if (!isNaN(timestamp) && timestamp > 0) {
          lastCheckTimeRef.current = timestamp;
          lastCheckCompliantRef.current = storage.getItem(LAST_CHECK_COMPLIANT_KEY) === 'true';
        }
      }
    } catch (error) {
      console.warn('[GeoComply Timing] Failed to load last check from localStorage', error);
    }
  }, []);

  /**
   * Record the outcome of a check: when it happened and whether it passed.
   *
   * The result is persisted alongside the timestamp so that after a page refresh we can
   * still tell "checked recently and passed" apart from "checked recently and failed".
   * A failed check must never stand in for a fresh one.
   */
  const recordCheck = useCallback((isCompliant: boolean) => {
    const now = Date.now();
    lastCheckTimeRef.current = now;
    lastCheckCompliantRef.current = isCompliant;

    try {
      storage.setItem(LAST_CHECK_TIME_KEY, now.toString());
      storage.setItem(LAST_CHECK_COMPLIANT_KEY, isCompliant ? 'true' : 'false');
    } catch (error) {
      console.warn('[GeoComply Timing] Failed to persist last check to localStorage', error);
    }
  }, []);

  /**
   * Clear the persisted last check from localStorage
   */
  const clearPersistedCheck = useCallback(() => {
    try {
      storage.removeItem(LAST_CHECK_TIME_KEY);
      storage.removeItem(LAST_CHECK_COMPLIANT_KEY);
    } catch (error) {
      console.warn('[GeoComply Timing] Failed to clear persisted check from localStorage', error);
    }
  }, []);

  /**
   * Check if 20 minutes have passed since the last check
   * Returns true if check should be performed, false if it's too soon
   */
  const shouldPerformCheck = useCallback((): boolean => {
    if (!lastCheckTimeRef.current) {
      // No previous check, should perform check
      return true;
    }

    const timeSinceLastCheck = Date.now() - lastCheckTimeRef.current;
    const intervalMs = checkIntervalMinutes * 60 * 1000;
    const timeUntilNextCheck = intervalMs - timeSinceLastCheck;

    return timeUntilNextCheck <= 0;
  }, [checkIntervalMinutes]);

  /**
   * Whether the last check passed and is recent enough to stand in for a new one
   */
  const hasRecentCompliantCheck = useCallback((): boolean => {
    return lastCheckCompliantRef.current === true && !shouldPerformCheck();
  }, [shouldPerformCheck]);

  /**
   * Internal function to schedule the next periodic check
   * This avoids circular dependencies by not calling performCheck directly
   */
  const scheduleNextPeriodicCheck = useCallback(() => {
    // Clear any existing timeout
    if (nextCheckTimeoutRef.current) {
      clearTimeout(nextCheckTimeoutRef.current);
      nextCheckTimeoutRef.current = null;
    }

    // Only schedule if we have a callback registered
    if (!periodicCheckCallbackRef.current) {
      return;
    }

    const intervalMs = checkIntervalMinutes * 60 * 1000;
    nextCheckTimeoutRef.current = setTimeout(async () => {
      // Perform periodic check using the callback
      if (periodicCheckCallbackRef.current) {
        // Call checkGeolocation directly to get full result including location
        const result = await checkGeolocation();
        const isCompliant = result.success && result.isCompliant;
        // Update last check for periodic checks too
        recordCheck(isCompliant);
        // Schedule next check
        scheduleNextPeriodicCheck();
        // Call the callback with location data and failure reasons
        periodicCheckCallbackRef.current(isCompliant, result.location, result.failureReasons);
      }
    }, intervalMs);
  }, [checkGeolocation, checkIntervalMinutes, recordCheck]);

  /**
   * Check if GeoComply should be bypassed
   * Returns true if:
   * 1. Running on localhost (development mode), OR
   * 2. storage.SKIP_GEOCOMPLY is set to 'true' (console override)
   *
   * Console usage: storage.setItem('SKIP_GEOCOMPLY', 'true')
   * To re-enable: storage.removeItem('SKIP_GEOCOMPLY')
   */
  const isLocalhost = (): boolean => {
    if (typeof window === 'undefined') return false;

    // Check for localStorage override flag (persists across page refresh)
    try {
      if (storage.getItem('SKIP_GEOCOMPLY') === 'true') {
        return true;
      }
    } catch {
      // localStorage not available
    }

    const hostname = window.location.hostname;
    const port = window.location.port;
    return (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      (hostname === 'localhost' && port === '8080') ||
      window.location.href.includes('localhost:8080')
    );
  };

  /**
   * Perform a geolocation check and update the timing
   * 
   * This should be called whenever a geolocation check is needed:
   * - After registration
   * - On login
   * - Before purchase
   * - Before redemption
   * - When entering gameplay
   * 
   * After each check, it updates the last check time and schedules the next check 20 minutes later.
   *
   * @param action - Description used in the failure toast, e.g. "make a purchase"
   * @param options.skipIfRecentlyChecked - Reuse the last check when it passed less than
   *   20 minutes ago instead of prompting the user again. Only for actions that already
   *   sit behind the login and periodic checks (purchase, redemption) — never for login.
   */
  const performCheck = useCallback(async (
    action: string = 'continue',
    options: { skipIfRecentlyChecked?: boolean } = {}
  ): Promise<boolean> => {
    // The user is already covered by a passing check from the last 20 minutes, so don't
    // put another location prompt in front of them
    if (options.skipIfRecentlyChecked && hasRecentCompliantCheck()) {
      return true;
    }

    // If running on localhost, return compliant immediately
    if (isLocalhost()) {
      // Record the check so timing logic works correctly
      recordCheck(true);
      return true;
    }

    if (!isInitialized) {
      console.warn('[GeoComply Timing] SDK not initialized, skipping check');
      return false;
    }

    // Perform the check
    const isCompliant = await performGeolocationCheck(checkGeolocation, action);

    recordCheck(isCompliant);

    // Schedule next check 20 minutes from now
    scheduleNextPeriodicCheck();

    return isCompliant;
  }, [isInitialized, checkGeolocation, scheduleNextPeriodicCheck, recordCheck, hasRecentCompliantCheck]);

  /**
   * Schedule the next periodic check 20 minutes after the last check
   * 
   * This is used by GeoComplyProvider to set up periodic checks.
   * NOTE: This does NOT perform an immediate check - it only schedules the next one.
   * If a check was just performed (e.g., on login), it will schedule 20 minutes from that check.
   * 
   * @param onPeriodicCheck - Optional callback to handle periodic check results
   */
  const scheduleNextCheck = useCallback((onPeriodicCheck?: (isCompliant: boolean) => void) => {
    // Store the callback for periodic checks
    if (onPeriodicCheck) {
      periodicCheckCallbackRef.current = onPeriodicCheck;
    }

    // Clear any existing timeout
    if (nextCheckTimeoutRef.current) {
      clearTimeout(nextCheckTimeoutRef.current);
      nextCheckTimeoutRef.current = null;
    }

    // If no check has been performed yet, don't schedule immediately
    // Wait for the first check to happen (from login/register/etc), then schedule the next one
    if (!lastCheckTimeRef.current) {
      return;
    }

    // Calculate time until next check (20 minutes after last check)
    const timeSinceLastCheck = Date.now() - lastCheckTimeRef.current;
    const intervalMs = checkIntervalMinutes * 60 * 1000;
    const timeUntilNextCheck = intervalMs - timeSinceLastCheck;

    if (timeUntilNextCheck <= 0) {
      // More than 20 minutes have passed, check immediately
      // Call checkGeolocation directly to get full result including location
      checkGeolocation().then((result) => {
        const isCompliant = result.success && result.isCompliant;
        recordCheck(isCompliant);
        // Schedule next check
        scheduleNextPeriodicCheck();
        // Call the callback with location data and failure reasons
        if (periodicCheckCallbackRef.current) {
          periodicCheckCallbackRef.current(isCompliant, result.location, result.failureReasons);
        }
      });
    } else {
      // Schedule check for the remaining time
      nextCheckTimeoutRef.current = setTimeout(async () => {
        // Call checkGeolocation directly to get full result including location
        const result = await checkGeolocation();
        const isCompliant = result.success && result.isCompliant;
        recordCheck(isCompliant);
        // Schedule next check
        scheduleNextPeriodicCheck();
        // Call the callback with location data and failure reasons
        if (periodicCheckCallbackRef.current) {
          periodicCheckCallbackRef.current(isCompliant, result.location, result.failureReasons);
        }
      }, timeUntilNextCheck);
    }
  }, [checkGeolocation, checkIntervalMinutes, scheduleNextPeriodicCheck, recordCheck]);

  /**
   * Reset timing (e.g., when user logs out)
   */
  const resetTiming = useCallback(() => {
    lastCheckTimeRef.current = null;
    lastCheckCompliantRef.current = null;
    clearPersistedCheck();
    if (nextCheckTimeoutRef.current) {
      clearTimeout(nextCheckTimeoutRef.current);
      nextCheckTimeoutRef.current = null;
    }
  }, [clearPersistedCheck]);

  /**
   * Get time until next check (for debugging/monitoring)
   */
  const getTimeUntilNextCheck = useCallback((): number | null => {
    if (!lastCheckTimeRef.current) return null;
    const timeSinceLastCheck = Date.now() - lastCheckTimeRef.current;
    const intervalMs = checkIntervalMinutes * 60 * 1000;
    const timeUntilNextCheck = intervalMs - timeSinceLastCheck;
    return Math.max(0, timeUntilNextCheck);
  }, []);

  return {
    performCheck,
    scheduleNextCheck,
    resetTiming,
    getTimeUntilNextCheck,
    shouldPerformCheck,
    isInitialized,
  };
}

